import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CourtContext, CourtContextValue, CourtVenue, BookingCreateInput, OrderSummary, FnbOutlet } from '@riptik/court-backoffice-module';
import { useTenant } from '../contexts/TenantContext';
import { useTranslation } from '../hooks/useTranslation';
import { useEventContext } from '../contexts/EventContext';
import {
  listVenues,
  request as courtRequest,
  createCourtWithProduct,
  createBooking,
  markBookingPaid,
  getRecurringPendingPeriod,
  recordRecurringPackagePayment,
  staffPinVerify,
  hasValidStaffSession,
  clearStaffSession,
} from '../api/client-court';
import { OrdersService } from '../api/generated';
import { FnbService } from '../api/generated-fnbpos';

/**
 * Riptik Court (sân pickleball) Provider
 *
 * Provider này THUỘC VỀ backoffice (parent project) — implement CourtContextValue, mirror
 * FnbPosProvider.tsx 1:1 (cùng nguồn tenant/i18n/getOrgUrl). tenantId Court = organization_slug (ánh xạ
 * 1:1, cùng quyết định 2026-07-19 như F&B/POS) — KHÔNG có bảng mapping riêng.
 *
 * Venue: host (Provider này) SỞ HỮU cache — module chỉ đọc venues/venuesLoading + gọi refreshVenues.
 *
 * getOrderUrl: build URL tới trang Order details HIỆN CÓ của host (route
 * `/organizations/:organizationSlug/events/:eventSlug/orders/:orderCode`, xem AppRoutes.tsx dòng
 * ~410-413 `<Route path="orders"><Route path=":orderCode" element={<OrderDetailsPage />} /></Route>`
 * lồng trong `:eventSlug`) — KHÔNG phải route `fnbpos/orders/:orderId` (đó là order F&B/POS nội bộ,
 * khác order vé của event). BookingsPage của module chỉ hiển thị link này, KHÔNG có nút cancel — path
 * cancel booking đúng đắn là flow CancelOrder sẵn có của trang Order, không đi qua court proxy.
 */
export const CourtProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tenant = useTenant();
  const { t } = useTranslation();
  const { getOrgUrl } = useEventContext();

  const [venues, setVenues] = useState<CourtVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);

  const organizationSlug = tenant?.organization_slug;

  const fetchVenues = useCallback(async (): Promise<void> => {
    if (!organizationSlug) return;
    setVenuesLoading(true);
    try {
      const rows = await listVenues(organizationSlug);
      setVenues(
        Array.isArray(rows)
          ? rows.map((r) => ({ supplierId: r.supplierId, name: r.name, eventSlug: r.eventSlug }))
          : []
      );
    } catch (error) {
      console.error('CourtProvider: fetch venues failed', error);
      setVenues([]);
    } finally {
      setVenuesLoading(false);
    }
  }, [organizationSlug]);

  useEffect(() => {
    void fetchVenues();
  }, [fetchVenues]);

  const contextValue = useMemo<CourtContextValue | null>(() => {
    if (!organizationSlug) return null;

    return {
      tenantId: organizationSlug,
      organizationSlug,

      locale: 'en',
      availableLocales: tenant?.supported_languages?.map((l: { code: string }) => l.code) || ['en', 'vi'],
      t,

      getOrgUrl,
      // Order details của event — xem doc-block ở đầu file. organizationSlug đã nằm trong getOrgUrl's
      // base nên chỉ cần ghép phần path còn lại.
      getOrderUrl: (eventSlug: string, orderRef: string): string =>
        getOrgUrl(`events/${encodeURIComponent(eventSlug)}/orders/${encodeURIComponent(orderRef)}`),

      venues,
      venuesLoading,
      refreshVenues: fetchVenues,

      api: {
        request: async <T,>(
          supplierId: number | string,
          path: string,
          body?: Record<string, unknown>,
          method?: string
        ): Promise<T> => courtRequest<T>(organizationSlug, supplierId, path, body, method),
      },

      onCreateCourt: async (supplierId: number, courtInput: Record<string, unknown>): Promise<void> => {
        const venue = venues.find((v) => v.supplierId === supplierId);
        if (!venue) {
          throw new Error(`Không tìm thấy venue supplierId=${supplierId} trong cache`);
        }
        await createCourtWithProduct(organizationSlug, supplierId, venue.eventSlug, courtInput);
      },

      onCreateBooking: async (supplierId: number, input: BookingCreateInput): Promise<{ orderCode: string }> => {
        const venue = venues.find((v) => v.supplierId === supplierId);
        if (!venue) {
          throw new Error(`Không tìm thấy venue supplierId=${supplierId} trong cache`);
        }
        return createBooking(organizationSlug, supplierId, venue.eventSlug, input);
      },

      onMarkBookingPaid: async (supplierId: number, orderCode: string): Promise<void> => {
        const venue = venues.find((v) => v.supplierId === supplierId);
        if (!venue) {
          throw new Error(`Không tìm thấy venue supplierId=${supplierId} trong cache`);
        }
        await markBookingPaid(organizationSlug, venue.eventSlug, orderCode);
      },

      // [v5, "1 order, nhiều payment, thu dần"] Order thuộc gói đặt sân định kỳ bị booking-core CHẶN gọi
      // onMarkBookingPaid ở trên — dùng route riêng, backend tự resolve đúng kỳ đang chờ (module KHÔNG gửi
      // period cụ thể, xem CourtContext.tsx comment onRecordRecurringPayment).
      onRecordRecurringPayment: async (supplierId: number, orderCode: string): Promise<void> => {
        const venue = venues.find((v) => v.supplierId === supplierId);
        if (!venue) {
          throw new Error(`Không tìm thấy venue supplierId=${supplierId} trong cache`);
        }
        await recordRecurringPackagePayment(organizationSlug, venue.eventSlug, orderCode);
      },

      // Timeline tổng quan đặt sân (BookingsPage) — tra tên/liên hệ khách theo order_ref. Đã có sẵn client
      // generated (OrdersService.getOrganizersEventsOrders1, GET .../orders/{code}), không cần route mới.
      // attendeeName lấy dòng position ĐẦU TIÊN có attendee_name (đủ dùng cho hiển thị timeline — không
      // cần phân biệt nhiều người tham dự trong 1 order ở màn overview này).
      //
      // [v5] pendingPeriod — gọi thêm GET recurring_pending_period (route mới, best-effort: lỗi ở đây
      // KHÔNG được làm hỏng toàn bộ getOrderSummary — timeline vẫn phải hiện được tên/trạng thái order dù
      // lookup gói định kỳ tạm thời lỗi, chỉ mất đúng phần "hiện đúng kỳ đang chờ").
      getOrderSummary: async (eventSlug: string, orderRef: string): Promise<OrderSummary> => {
        const order = await OrdersService.getOrganizersEventsOrders1(organizationSlug, eventSlug, orderRef);
        const attendeeName = order.positions?.find((p) => p.attendee_name)?.attendee_name;
        let pendingPeriod: OrderSummary['pendingPeriod'];
        try {
          const pending = await getRecurringPendingPeriod(organizationSlug, eventSlug, orderRef);
          if (pending.isRecurring && pending.periodId && pending.amountMinor !== undefined) {
            pendingPeriod = { periodIndex: pending.periodIndex ?? 0, amountMinor: pending.amountMinor };
          }
        } catch (err) {
          console.warn('[court] getRecurringPendingPeriod failed (không chặn getOrderSummary)', err);
        }
        return { attendeeName, email: order.email, phone: order.phone, orderStatus: order.status, pendingPeriod };
      },

      // staffAuth — xem plan "Staff/Phân quyền cho module Court" §1.6. Host SỞ HỮU việc lưu token
      // (sessionStorage theo supplierId, xem client-court.ts) — module chỉ gọi 3 hàm này qua Context,
      // không tự biết cơ chế lưu trữ (đúng nguyên tắc DIP đã áp dụng cho mọi callback khác trong file này).
      staffAuth: {
        verifyPin: (supplierId: string, pin: string) => staffPinVerify(organizationSlug, supplierId, pin),
        hasValidSession: (supplierId: string) => hasValidStaffSession(supplierId),
        clearSession: (supplierId: string) => clearStaffSession(supplierId),
      },

      // listFnbOutlets — gọi THẲNG generated client fnbpos (KHÔNG qua useFnbPosContext/React hook) vì
      // CourtProvider và FnbPosProvider được mount ở 2 nhánh route RIÊNG (xem AppRoutes.tsx) — không có gì
      // đảm bảo FnbPosProvider là ancestor của cây component Court, gọi hook đó ở đây sẽ throw ("must be
      // used within FnbPosProvider"). API generated là client thuần, độc lập cây React, an toàn gọi từ bất
      // kỳ đâu — mirror đúng cách `createBooking` (client-court.ts) đã gọi thẳng `OrdersService` không qua
      // context nào khác.
      listFnbOutlets: async (): Promise<FnbOutlet[]> => {
        const resp = await FnbService.outletsList(organizationSlug, {});
        const rows = Array.isArray(resp?.data) ? (resp.data as unknown[]) : [];
        return rows.map((r) => {
          const row = r as { id: number; name: string };
          return { id: row.id, name: row.name };
        });
      },
    };
  }, [organizationSlug, tenant, t, getOrgUrl, venues, venuesLoading, fetchVenues]);

  if (!contextValue) {
    return null;
  }

  return <CourtContext.Provider value={contextValue}>{children}</CourtContext.Provider>;
};
